/**
 * Car search parameters — the one place a resolved car becomes search filters.
 *
 * Every plate-driven entry point builds its query here: the product picker in
 * <RegNumInput />, the size chips in <CarInfoSidebar />, and the legacy list in
 * <CarInfoCard />. Before this module each of the three composed its own URL,
 * which is why only the tyre tab ever returned anything (BR-4).
 *
 * Two rules shape everything below.
 *
 * 1. A car narrows the search by FITMENT, never by name. The car's make and
 *    model ride along as `carMake` / `carModel` — plain UI params that the
 *    Marketplace API never sees (SearchPage.duck strips them). They rank
 *    matches and label the "Din bil" chip; they do not hide anything (BR-2).
 *    `pub_categoryLevel1` / `pub_categoryLevel2` stay available as ordinary
 *    filters the buyer can pick by hand.
 *
 * 2. Only fields sellers actually fill may narrow on their own. Measured on the
 *    live marketplace (2026-08-10, rim listings):
 *
 *      diameter 100%   etFront 96%   numberOfBolts 98%   boltCircle 98%
 *      rimWidthFront 2%            hubHole 3%
 *
 *    So width and hub bore are marked RELAXABLE: they are applied, shown as
 *    chips and honoured whenever they match something, but a search that finds
 *    nothing with them re-runs without them rather than showing a bare "inga
 *    resultat" (see SearchPage.duck.js). BR-6 asked for diameter + width +
 *    offset + hub bore; this delivers all four without recreating the empty
 *    result the ticket is about.
 *
 * NOTE on `combination`: it is not a listing field in the Console config, but
 * it IS on ~99% of listings, so the API happily filters on it and drops every
 * listing that lacks the exact string. `pub_combination=has_any:<every size>`
 * on the plate redirect is what made 93% of car searches return nothing
 * (BR-1). It survives in exactly one place — the deliberate "komplett sats"
 * choice for a staggered car (P25009-364) — where covering both axles cannot
 * be expressed any other way.
 */

import { boltCircleSpellings } from './boltCircleCore';
import { boreFitsHub, wheelDiametersOfCar } from './wheelFitment';

/* Filter params the car search owns. Cleared before every apply so switching
 * tab or size can never leave a contradictory mix behind. */
export const CAR_FILTER_KEYS = [
  'pub_adType',
  'pub_tireWidthFront',
  'pub_tireWidthRear',
  'pub_profileFront',
  'pub_profileRear',
  'pub_rimWidthFront',
  'pub_rimWidthRear',
  'pub_diameter',
  'pub_numberOfBolts',
  'pub_boltCircle',
  'pub_etFront',
  'pub_hubHole',
  'pub_combination',
  'axle',
];

/* Car identity params. Not filters — read by the ranking pass and the car
 * bar, stripped before the Marketplace API call. `carId` names a car chosen by
 * model, without a plate (its car-database id, P25009-491), the way
 * `regNumber` names a plate car. */
export const CAR_CONTEXT_KEYS = ['regNumber', 'carId', 'carMake', 'carModel'];

/* The two that say WHICH car the search is about. A tab or size click keeps
 * them; only a new car replaces them (carSearchLocation.carSearchQuery). */
export const CAR_IDENTITY_KEYS = ['regNumber', 'carId'];

/**
 * Conditions a car search gives up, in order, rather than show nothing.
 *
 * Each entry is one step. Keys inside a step go together — dropping a tyre
 * width while keeping its profile would ask for something nobody sells.
 *
 * The order is "least informative first", and what a buyer actually chose
 * goes last:
 *   1. hub bore   — filled in on 3% of rim listings, so it mostly excludes
 *                   stock over a measurement nobody recorded
 *   2. ET         — well filled in and it does its job, but the window is
 *                   one-sided by client decision (the car's offset down to
 *                   10 mm below it), so rims that fit fine sit just above it
 *   3. the size the buyer clicked — rim width, or tyre size on a complete
 *                   wheel. Given up only when nothing else is left. The
 *                   diameter is never part of this step, so what comes back
 *                   is always the right wheel size.
 *
 * Rim width was first here until the backfill ran: it was on 2% of listings,
 * which made it a filter that excluded almost everything and told the shopper
 * almost nothing. Now that it is on 91%, dropping it first would throw away
 * the one thing the shopper asked for and answer a 7" search with 8.5" rims.
 *
 * Two things are never given up. The bolt pattern, because a 5x108 wheel does
 * not go on a 5x112 car at any offset. And the tyre size on the Däck tab,
 * because there a tyre IS its size — a 215/55R17 is not a loose match for a
 * 235/45R18, it is a different product.
 */
const RELAXATION_STEPS = [
  { keys: ['pub_hubHole'] },
  { keys: ['pub_etFront'] },
  { keys: ['pub_rimWidthFront'] },
  { keys: ['pub_tireWidthFront', 'pub_profileFront'], adTypes: ['wheel'] },
];

/**
 * The steps this particular query can give up, in order — a step only counts
 * when the query actually carries its keys and the product type allows it.
 *
 * @param {Object} params the search params about to be sent
 * @returns {Array<Array<string>>} key groups, in the order they may be dropped
 */
export const relaxationSteps = (params = {}) =>
  RELAXATION_STEPS.filter(
    step =>
      (!step.adTypes || step.adTypes.includes(params.pub_adType)) &&
      step.keys.some(key => params[key] !== undefined)
  ).map(step => step.keys.filter(key => params[key] !== undefined));

/* How far below the car's original offset a wheel may sit. The client's rule:
 * "ET should be filtered from original value -10". */
export const ET_WINDOW = 10;

const clean = value => (typeof value === 'string' ? value.trim() : '');

const toNumber = value => {
  if (value === null || value === undefined || value === '') return null;
  const n = parseFloat(String(value).replace(/[,_]/g, '.'));
  return Number.isFinite(n) ? n : null;
};

/* Pick the per-car object regardless of response envelope:
 *   licence-plate API   { result: {...} }  or  { car: {...} }
 *   fitment DB          { data: [{...}] } */
const pickCar = data => {
  if (!data || typeof data !== 'object') return {};
  return data.car || (Array.isArray(data.data) ? data.data[0] : null) || data.result || data || {};
};

/**
 * A listing field's value can be written two ways: the Console enum key uses
 * an underscore ("114_3", "7_5") while imported listings keep the decimal
 * point ("114.3", "7.5"). A comma-separated list is an OR, so sending both
 * costs nothing and stops a correct listing being missed over punctuation.
 */
const decimalVariants = value => {
  const n = toNumber(value);
  if (n === null) return '';
  const plain = String(n);
  return plain.includes('.') ? `${plain},${plain.replace('.', '_')}` : plain;
};

/**
 * The fitment facts a search can be built from.
 *
 * @param {Object} data raw plate / fitment payload
 * @returns {Object} { bolts, pcd, centreBore, offset, make, model, displayName }
 *   with null for anything the payload does not carry.
 */
export const carFitment = data => {
  const car = pickCar(data);
  const tech = car?.technical || {};
  const tireInfo = car?.tire_info || {};

  const bolts = toNumber(tech.stud_holes ?? tireInfo.bolts);
  const pcd = toNumber(tech.pcd ?? tireInfo.bolt_circle);
  const centreBore = toNumber(tech.centre_bore ?? tireInfo.center_bore);

  /* Offset: the front wheel's rim_offset when the fitment DB supplies it,
   * otherwise parsed out of an OEM rim string like "7Jx17 ET49". */
  const frontWheel = car?.wheels?.[0]?.front;
  let offset = toNumber(frontWheel?.rim_offset);
  if (offset === null) {
    const rimString = clean(car?.original_tires?.front_rims) || clean(frontWheel?.rim);
    const match = /ET\s*(-?\d+(?:[.,]\d+)?)/i.exec(rimString);
    offset = match ? toNumber(match[1]) : null;
  }

  const makeName = car?.make?.name || car?.brand || data?.result?.make || '';
  const modelName =
    car?.model?.name ||
    car?.series?.name ||
    (typeof car?.series === 'string' ? car.series : '') ||
    (typeof car?.model === 'string' ? car.model : '') ||
    car?.sales_name ||
    data?.result?.model ||
    '';

  const slug = value =>
    String(value || '')
      .toLowerCase()
      .replace(/\s+/g, '')
      .replace(/[^a-z0-9_-]/g, '');

  return {
    bolts,
    pcd,
    centreBore,
    offset,
    make: car?.make?.slug ? String(car.make.slug) : slug(makeName),
    model: car?.model?.slug ? String(car.model.slug) : slug(modelName),
    displayName: [makeName, modelName].filter(Boolean).join(' ').trim(),
  };
};

/**
 * Hub bore is a "big enough" requirement, not an equality: a rim whose centre
 * bore is wider than the car's hub is fitted with a centring ring, a narrower
 * one cannot go on at all. So the filter is every configured option from the
 * car's value upwards — the client's "from original value to max value in the
 * navhål container" — less the fit check's 0.1 mm rounding tolerance.
 *
 * @param {number} centreBore the car's hub diameter in mm
 * @param {Array} enumOptions hubHole options from the hosted listing config
 * @returns {string} comma-separated option keys, or '' when nothing qualifies
 */
export const hubHoleOptionsFrom = (centreBore, enumOptions) => {
  const cb = toNumber(centreBore);
  if (cb === null || !Array.isArray(enumOptions)) return '';
  return enumOptions
    .map(option => String(option?.option ?? option))
    .filter(key => {
      const n = toNumber(key);
      /* "other" and friends parse to null — they carry no measurement, so they
       * can neither pass nor fail a "wide enough" test. Leave them out.
       * boreFitsHub allows the fit check's 0.1 mm: Volvo's 63.4 hub takes the
       * 63.3 bore every Volvo package lists, and the search used to drop
       * those wheels before the fit check could pass them (P25009-492). */
      return n !== null && boreFitsHub(n, cb);
    })
    .join(',');
};

/** "35,45" — the offset window, from the car's ET minus ET_WINDOW up to it. */
export const etRangeFrom = offset => {
  const et = toNumber(offset);
  if (et === null) return '';
  return `${Math.round(et - ET_WINDOW)},${Math.round(et)}`;
};

/* "235/55R20" / "255/50ZR20 101T" → { width, profile, diameter } */
export const parseTireSize = size => {
  const match = /(\d+)\/(\d+)\s*Z?R(\d+)/i.exec(clean(size));
  return match ? { width: match[1], profile: match[2], diameter: match[3] } : null;
};

/* "7.5Jx18 ET50.5" / "7JX17" / "18 ET45" → { width, diameter, offset } */
export const parseRimSize = size => {
  const text = clean(size);
  if (!text) return null;
  const full = /^([\d.,]+)\s*J?\s*[xX]\s*(\d+)/.exec(text);
  const diameterOnly = full ? null : /^(\d+)\s*ET/i.exec(text);
  const diameter = full ? full[2] : diameterOnly ? diameterOnly[1] : null;
  if (!diameter) return null;
  const offsetMatch = /ET\s*(-?[\d.,]+)/i.exec(text);
  return {
    width: full ? String(toNumber(full[1])) : null,
    diameter,
    offset: offsetMatch ? toNumber(offsetMatch[1]) : null,
  };
};

/**
 * Build the search params for one car + product tab (+ optional size).
 *
 * Tyres are matched on dimension alone — a tyre fits any car running that
 * size, and a bolt pattern or hub bore on a tyre listing means nothing
 * (BR-3). Rims and complete wheels add the car's fitment: bolt pattern,
 * offset window, hub bore, and for rims the rim width.
 *
 * Without a size (the Hjul / Däck / Fälg tab) the search still keeps to the
 * wheel diameters the car runs. Before P25009-492 it asked for none, so a
 * car whose smallest wheel is 18" got a page of 16" and 17" wheels that the
 * fit check then hid — "0 resultat" with fitting stock on page 2.
 *
 * @param {Object} options
 * @param {Object} options.data       plate / fitment payload
 * @param {string} options.adType     'wheel' | 'tire' | 'rim'
 * @param {string} [options.size]     the clicked size ("235/45R18", "7Jx17 ET49")
 * @param {Array}  [options.hubHoleOptions] hubHole enumOptions from the config
 * @returns {Object} query params, values already URL-safe strings
 */
export const carSearchParams = ({ data, adType, size, hubHoleOptions } = {}) => {
  const fitment = carFitment(data);
  const params = { pub_adType: adType };

  const isTire = adType === 'tire';
  const isRim = adType === 'rim';

  if (size) {
    if (isRim) {
      const rim = parseRimSize(size);
      if (rim) {
        params.pub_diameter = rim.diameter;
        /* The width the buyer clicked wins over the car's default — a car
         * usually offers several widths on the same diameter. */
        if (rim.width) params.pub_rimWidthFront = decimalVariants(rim.width);
        if (rim.offset !== null && rim.offset !== undefined) {
          params.pub_etFront = etRangeFrom(rim.offset);
        }
      }
    } else {
      const tire = parseTireSize(size);
      if (tire) {
        params.pub_tireWidthFront = tire.width;
        params.pub_profileFront = tire.profile;
        params.pub_diameter = tire.diameter;
      }
    }
  }

  if (!params.pub_diameter) {
    const diameters = wheelDiametersOfCar(pickCar(data));
    if (diameters.length > 0) params.pub_diameter = diameters.join(',');
  }

  if (!isTire) {
    if (fitment.bolts !== null) params.pub_numberOfBolts = String(fitment.bolts);
    /* Every stored spelling of the circle: Console's key for 139.7 is `1397`,
     * not "139_7", and older imports stored "139.7" (P25009-488). */
    if (fitment.pcd !== null) params.pub_boltCircle = boltCircleSpellings(fitment.pcd).join(',');
    if (!params.pub_etFront) {
      const et = etRangeFrom(fitment.offset);
      if (et) params.pub_etFront = et;
    }
    const hub = hubHoleOptionsFrom(fitment.centreBore, hubHoleOptions);
    if (hub) params.pub_hubHole = hub;
  }

  /* Car identity — ranking and the chip, never a filter (BR-2). */
  if (fitment.make) params.carMake = fitment.make;
  if (fitment.model) params.carModel = fitment.model;

  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== '' && value !== null && value !== undefined)
  );
};

/* The tiretype value the season filter writes for "Vinter" (DimensionQuickSearch). */
export const WINTER_TIRETYPE = 'winterFriction,winterDubbed';

/** October to March — the months a Swedish car search starts on winter. */
export const isWinterSeason = (date = new Date()) => {
  const month = date.getMonth();
  return month >= 9 || month <= 2;
};

/**
 * A NEW car search starts on "Vinter" from October to March (P25009-486 BR-1):
 * a buyer who types a plate in November wants winter wheels. Only for tyres
 * and complete wheels — a rim has no season — and only when the query does
 * not name a season already. Called where a car search starts from nothing
 * (the plate picker, the landing search, the remembered car), never on a tab
 * or size click, so a buyer who removed the chip keeps it removed.
 *
 * @param {Object} params query params about to be pushed
 * @param {Date} [date] today, for tests
 * @returns {Object} the params, with pub_tiretype added when it applies
 */
export const withSeasonDefault = (params = {}, date = new Date()) => {
  const adType = params.pub_adType;
  if (adType !== 'wheel' && adType !== 'tire') return params;
  if (params.pub_tiretype || !isWinterSeason(date)) return params;
  return { ...params, pub_tiretype: WINTER_TIRETYPE };
};

/**
 * Apply a car search onto an existing query string.
 *
 * Every key the car search owns is cleared first, then the new set is written.
 * Params the car search does not own (page, sort, price, county, …) survive —
 * except the tyre season on a rim search: a rim has no season, and a
 * leftover "Vinter" there would hide every rim.
 *
 * @param {string} search current location.search
 * @param {Object} params from carSearchParams()
 * @returns {string} the next search string, leading "?" included
 */
export const applyCarSearch = (search, params) => {
  const next = new URLSearchParams(search || '');
  [...CAR_FILTER_KEYS, ...CAR_CONTEXT_KEYS.filter(k => !CAR_IDENTITY_KEYS.includes(k))].forEach(
    key => next.delete(key)
  );
  if (params?.pub_adType === 'rim') next.delete('pub_tiretype');
  next.delete('page');
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === '' || value === null || value === undefined) return;
    next.set(key, String(value));
  });
  return `?${next.toString()}`;
};

/**
 * Rank listings against the car: the ones tagged with this model first, then
 * this make, then everything else. Order inside each group is untouched, so
 * the marketplace's own sort still decides among equals (BR-2, AC-2).
 *
 * @param {Array} listings loaded listings
 * @param {Object} car { make, model } — the lowercased slugs from the URL
 * @returns {Array} a re-ordered copy; the input array is not modified
 */
export const rankListingsForCar = (listings, car) => {
  const make = String(car?.make || '').toLowerCase();
  const model = String(car?.model || '').toLowerCase();
  if (!Array.isArray(listings) || (!make && !model)) return listings;

  const score = listing => {
    const data = listing?.attributes?.publicData || {};
    const listingMake = String(data.categoryLevel1 || '').toLowerCase();
    const listingModel = String(data.categoryLevel2 || '').toLowerCase();
    if (model && listingModel === model) return 0;
    if (make && listingMake === make) return 1;
    return 2;
  };

  return listings
    .map((listing, index) => ({ listing, index, score: score(listing) }))
    .sort((a, b) => a.score - b.score || a.index - b.index)
    .map(entry => entry.listing);
};
